// src/components/Board/index.jsx
import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Pagination, Button, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../api/axios';
import { fetchPosts } from '../../api/posts';
import BoardItem from './BoardItem';
import BoardSearchBar from './BoardSearchBar';
import BoardPagination from './BoardPagination';
import { useUser } from '../../contexts/UserContext';

export default function Board() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || '';
  const authorId = searchParams.get('author_id') || '';   // ✅ 추가
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const searchParam = searchParams.get('search') || '';

  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParam);
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryName, setCategoryName] = useState('📋 전체 게시판');
  const [canWrite, setCanWrite] = useState(true);
  const [readCost, setReadCost] = useState(0); // ✅ 추가: 카테고리 읽기 비용

  const { userInfo } = useUser();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!categoryId) {
      setCategoryName('📋 전체 게시판');
      setCanWrite(true);
      setReadCost(0); // ✅ 전체게시판이면 차감 없음
      return;
    }

    api.get('/api/boards/categories/')
      .then(res => {
        const cat = res.data.categories.find(c => c.id === +categoryId);
        setCategoryName(cat ? `${cat.emoji} ${cat.name}` : '❓ 알 수 없는 카테고리');

        if (!cat) return setCanWrite(false);
        setReadCost(cat.read_cost || 0); // ✅ 읽기 비용 저장

        const role = userInfo?.role || 'user';
        const permission = cat.write_permission || 'all';

        const allowed =
          permission === 'all' ||
          (permission === 'admin' && ['admin', 'master'].includes(role)) ||
          (permission === 'master' && role === 'master');

        setCanWrite(allowed);
      })
      .catch(() => {
        setCategoryName('⚠️ 카테고리 로드 실패');
        setCanWrite(false);
        setReadCost(0);
      });
  }, [categoryId, userInfo]);

  const loadPosts = useCallback(
    (page = 1, query = '') => {
      fetchPosts(page, query, categoryId, authorId) // ✅ authorId 추가
        .then(res => {
          setPosts(res.data.results || []);
          setTotalPages(res.data.total_pages || 1);
          setCurrentPage(res.data.current_page || page);
        })
        .catch(() => { setPosts([]); setTotalPages(1); setCurrentPage(page); });
    },
    [categoryId, authorId], // ✅ deps 추가
  );

  useEffect(() => {
    loadPosts(pageParam, searchParam);
  }, [loadPosts, pageParam, searchParam]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (categoryId) params.set('category', categoryId);
    if (authorId) params.set('author_id', authorId); // ✅ 추가
    params.set('page', '1');
    params.set('search', searchQuery.trim());
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams();
    if (categoryId) params.set('category', categoryId);
    if (authorId) params.set('author_id', authorId); // ✅ 추가
    params.set('search', searchParam);
    params.set('page', String(page));
    setSearchParams(params);
  };

  const writeLink = `/main/posts/create${categoryId ? `?category=${categoryId}` : ''}`;

  return (
    <Box sx={{
      px: { xs: 1.5, sm: 2, md: 4 },
      py: { xs: 2, sm: 3, md: 4 },
      border: 1, borderColor: 'grey.300',
      borderRadius: 2, bgcolor: 'background.paper',
    }}>
      {/* 타이틀 */}
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.125rem' },
          fontWeight: 'bold',
          mb: 0.5,
        }}
      >
        {categoryName}
      </Typography>

      {/* 🔥 읽기 비용 안내 문구 */}
      {readCost > 0 && (
        <Typography
          variant="caption"
          sx={{
            mb: 2,
            color: 'text.primary',
            textAlign: 'center',     // ✅ 가운데 정렬 (모든 화면에서)
            display: 'block',
            width: '100%',
          }}
        >
          글 열람 시 <strong>-{readCost}🌿</strong>가 소모됩니다.
        </Typography>
      )}

      {/* ─── MOBILE TOP ─── */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mb: 2,
            alignItems: 'center',
          }}
        >
          <BoardSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch}
          />
          {canWrite && (
            <Button fullWidth component={Link} variant="contained" to={writeLink}>
              게시글 작성
            </Button>
          )}
        </Box>
      )}

      {/* ─── DESKTOP TOP ─── */}
      {!isMobile && (
        <BoardSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
        />
      )}

      {/* 게시글 리스트 */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {posts.map(p => <BoardItem key={p.id} post={p} />)}
      </Box>

      {/* ─── BOTTOM AREA ─── */}
      {isMobile ? (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            size="small"
            siblingCount={0}
            boundaryCount={1}
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => handlePageChange(p)}
            color="primary"
          />
        </Box>
      ) : (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <BoardPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            canWrite={canWrite}
          />
        </Box>
      )}
    </Box>
  );
}
